import { useEffect, useState } from 'react';
import type { Child } from '@aba-denver/core';
import {
  listUsers,
  setUserDisabled,
  setUserRole,
  reassignPatient,
  ManagedUser,
  UserRole,
} from '../../lib/functions';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';

type PatientWithOwner = Child & { ownerId?: string };

export function Users() {
  const { user } = useAuth();
  const { children } = useData();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingUid, setPendingUid] = useState<string | null>(null);
  const [showReassign, setShowReassign] = useState(false);

  const load = async () => {
    setError(null);
    try {
      setUsers(await listUsers());
    } catch (err) {
      console.error(err);
      setError('Não foi possível carregar os usuários. Verifique se você tem acesso de administrador.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const withPending = async (uid: string, fn: () => Promise<void>) => {
    setPendingUid(uid);
    try {
      await fn();
      await load();
    } catch (err) {
      console.error(err);
      alert('Ação não concluída. Tente novamente.');
    } finally {
      setPendingUid(null);
    }
  };

  const toggleDisabled = (u: ManagedUser) => withPending(u.uid, () => setUserDisabled(u.uid, !u.disabled));
  const changeRole = (u: ManagedUser, role: UserRole) => withPending(u.uid, () => setUserRole(u.uid, role));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Usuários</h1>
          <p className="muted" style={{ margin: 0 }}>Gerencie terapeutas, permissões e pacientes.</p>
        </div>
        <button className="btn secondary" onClick={() => setShowReassign(true)}>
          Mover paciente
        </button>
      </div>

      {error && <div className="card" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>{error}</div>}

      <div className="card">
        {loading ? (
          <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
            <Spinner />
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Função</th>
                  <th>Pacientes</th>
                  <th>Situação</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.uid === user?.uid;
                  const busy = pendingUid === u.uid;
                  return (
                    <tr key={u.uid}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{u.displayName ?? u.email ?? u.uid}</div>
                        {u.displayName && <div className="muted" style={{ fontSize: 13 }}>{u.email}</div>}
                      </td>
                      <td>
                        <select
                          value={u.role}
                          disabled={busy || isSelf}
                          onChange={(e) => changeRole(u, e.target.value as UserRole)}
                          style={{ padding: '6px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}
                        >
                          <option value="therapist">Terapeuta</option>
                          <option value="admin">Administrador</option>
                        </select>
                      </td>
                      <td>{u.patientCount}</td>
                      <td>
                        {u.disabled ? <Badge variant="danger">Desativado</Badge> : <Badge variant="success">Ativo</Badge>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {!isSelf && (
                          <button
                            className={u.disabled ? 'btn secondary sm' : 'btn danger sm'}
                            disabled={busy}
                            onClick={() => toggleDisabled(u)}
                          >
                            {busy ? '…' : u.disabled ? 'Ativar' : 'Desativar'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showReassign && (
        <ReassignModal
          patients={children as PatientWithOwner[]}
          users={users}
          onClose={() => setShowReassign(false)}
          onDone={() => {
            setShowReassign(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

function ReassignModal({
  patients,
  users,
  onClose,
  onDone,
}: {
  patients: PatientWithOwner[];
  users: ManagedUser[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [patientId, setPatientId] = useState('');
  const [newOwnerId, setNewOwnerId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = patients.find((p) => p.id === patientId);
  const targets = users.filter((u) => !u.disabled && u.uid !== selected?.ownerId);

  const submit = async () => {
    if (!patientId || !newOwnerId) return setError('Selecione o paciente e o terapeuta de destino.');
    setBusy(true);
    setError(null);
    try {
      await reassignPatient(patientId, newOwnerId);
      onDone();
    } catch (err) {
      console.error(err);
      setError('Não foi possível mover o paciente.');
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Mover paciente"
      onClose={onClose}
      footer={
        <>
          <button className="btn secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button className="btn" onClick={submit} disabled={busy}>
            {busy ? 'Movendo…' : 'Mover'}
          </button>
        </>
      }
    >
      <div className="field">
        <label>Paciente</label>
        <select value={patientId} onChange={(e) => setPatientId(e.target.value)}>
          <option value="">Selecione…</option>
          {[...patients]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </select>
      </div>
      <div className="field">
        <label>Novo terapeuta</label>
        <select value={newOwnerId} onChange={(e) => setNewOwnerId(e.target.value)} disabled={!patientId}>
          <option value="">Selecione…</option>
          {targets.map((u) => (
            <option key={u.uid} value={u.uid}>
              {u.displayName ?? u.email}
            </option>
          ))}
        </select>
      </div>
      <p className="muted" style={{ fontSize: 13, margin: 0 }}>
        O paciente e todas as suas avaliações passarão para o terapeuta selecionado.
      </p>
      {error && <div className="field error" style={{ marginTop: 12 }}>{error}</div>}
    </Modal>
  );
}
